import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';

const LoginForm = ({ onLogin, isLoading = false, error = '', mockCredentials = null }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });

  // Auto-fill demo credentials for better UX
  useEffect(() => {
    if (mockCredentials) {
      setFormData(prev => ({
        ...prev,
        email: mockCredentials?.email,
        password: mockCredentials?.password
      }));
    }
  }, [mockCredentials]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    onLogin(formData);
  };

  return (
    <div className="w-full max-w-md">
      <div className="bg-card border border-subtle rounded-xl p-8 shadow-elevation-2">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-foreground mb-2">Welcome Back</h1>
          <p className="text-muted-foreground">
            Sign in to continue your testing journey
          </p>
          {mockCredentials && (
            <div className="mt-4 p-3 bg-muted/20 rounded-lg text-sm text-muted-foreground">
              <p className="font-medium">Demo Account:</p>
              <p>{mockCredentials?.email}</p>
              <p>Password: {mockCredentials?.password}</p>
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <Input
            type="email"
            name="email"
            label="Email Address"
            value={formData?.email}
            onChange={handleInputChange}
            required
            placeholder="Enter your email"
            disabled={isLoading}
          />

          <Input
            type="password"
            name="password"
            label="Password"
            value={formData?.password}
            onChange={handleInputChange}
            required
            placeholder="Enter your password"
            disabled={isLoading}
          />

          <div className="flex items-center justify-between">
            <Checkbox
              id="rememberMe"
              name="rememberMe"
              checked={formData?.rememberMe}
              onChange={handleInputChange}
              label="Remember me"
              disabled={isLoading}
            />
            <button
              type="button"
              className="text-sm text-primary hover:text-primary/80 transition-colors"
              onClick={() => alert('Password reset functionality would be implemented here')}
            >
              Forgot password?
            </button>
          </div>

          {error && (
            <div className="p-3 bg-error/10 border border-error/20 rounded-lg text-error text-sm">
              {error}
            </div>
          )}

          <Button
            type="submit"
            size="lg"
            className="w-full"
            disabled={isLoading}
            iconName={isLoading ? "Loader2" : "LogIn"}
            iconPosition="left"
            iconProps={isLoading ? { className: "animate-spin" } : {}}
          >
            {isLoading ? 'Signing In...' : 'Sign In'}
          </Button>
        </form>

        <div className="mt-8 text-center">
          <p className="text-muted-foreground text-sm">
            Don't have an account?{' '}
            <Link
              to="/user-registration"
              className="text-primary hover:text-primary/80 font-medium transition-colors"
            >
              Start learning for free
            </Link>
          </p>
        </div>

        <div className="mt-6 pt-6 border-t border-subtle">
          <div className="text-center">
            <p className="text-xs text-muted-foreground mb-2">
              Secure login with session management
            </p>
            <div className="flex items-center justify-center space-x-4 text-xs text-muted-foreground">
              <div className="flex items-center space-x-1">
                <Icon name="Shield" size={12} />
                <span>SSL Protected</span>
              </div>
              <div className="flex items-center space-x-1">
                <Icon name="Clock" size={12} />
                <span>Auto Session</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;